import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import * as React from "react";

import { useDomainServices } from "@/providers/DomainServices";
import type { PaginatedResponse } from "@/shared/http";

const DEFAULT_PAGE_SIZE = 50;

export interface PaginatedQueryResult<T> {
  /**
   * The current page's items, or the last page fetched while a forward jump is
   * still in flight. `undefined` only before the first page arrives.
   */
  data: T[] | undefined;
  /** `data` is an earlier page standing in for one still loading. */
  isPlaceholderData: boolean;
  error: Error | null;
  status: "pending" | "error" | "success";
  /** The current page has not arrived and more is still fetchable. */
  isLoading: boolean;
  /**
   * A fetch the user is waiting on: initial, advance, or refetch. Excludes the
   * speculative prefetch of the next page, which must not surface as loading.
   */
  isFetching: boolean;
  /**
   * `true` if there's a page after `currentPage`, either already
   * loaded in `pages[currentPage + 1]` or fetchable from the server.
   */
  hasNextPage: boolean;
}

/**
 * Page-aware results hook for cursor-paginated queries displayed in a
 * page based pagination UI (page 1, 2, 3, …).
 *
 * For "load more" UIs that want all pages concatenated, use
 * `useInfiniteQueryResults` instead.
 */
export function usePaginatedQueryResults<T>(
  queryId: string | undefined,
  currentPage: number,
  pageSize: number = DEFAULT_PAGE_SIZE,
): PaginatedQueryResult<T> {
  const { queryService } = useDomainServices();
  const validPage = currentPage >= 0;

  // Cursor pagination forces sequential fetches — page N's token comes from
  // N-1 — which is the chain `useInfiniteQuery` already models.
  const q = useInfiniteQuery({
    queryKey: ["queryResults", queryId, pageSize],
    queryFn: ({ pageParam, signal }) =>
      queryService.getQueryResults<T>(queryId!, {
        abortSignal: signal,
        nextPageToken: pageParam,
        pageSize,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last: PaginatedResponse<T>) =>
      last.next_token ?? undefined,
    enabled: !!queryId && validPage,
    placeholderData: keepPreviousData,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  React.useEffect(
    function prefetchNextPage() {
      if (!q.data || q.isFetchingNextPage || q.hasNextPage === false) {
        return;
      }

      const loaded = q.data.pages.length;
      if (currentPage >= loaded - 1) {
        void q.fetchNextPage();
      }
    },
    [currentPage, q.data, q.hasNextPage, q.isFetchingNextPage, q],
  );

  if (!validPage) {
    return {
      data: undefined,
      isPlaceholderData: false,
      error: new Error(`Invalid page index: ${currentPage}`),
      status: "error",
      isLoading: false,
      isFetching: false,
      hasNextPage: false,
    };
  }

  const pages = q.data?.pages;
  const loaded = pages?.length;

  // `q.hasNextPage` is about the loaded tail, not about `currentPage`.
  const hasNextPage =
    (loaded !== undefined && currentPage + 1 < loaded) || q.hasNextPage;

  const items = pages?.[currentPage]?.items;

  // Pages accumulate in fetch order, so the tail is the newest one fetched —
  // what the user was looking at before jumping past the prefetch. Standing in
  // with it keeps rows on screen instead of collapsing the table to empty.
  const lastFetched =
    pages === undefined ? undefined : pages[pages.length - 1]?.items;

  // `fetchNextPage` serves both the speculative warm of page N+1 and a page the
  // user has navigated to. Only the page on screen separates them: rows mean
  // nobody is waiting.
  const isAwaitingCurrentPage = items === undefined;
  const isPrefetching = q.isFetchingNextPage && !isAwaitingCurrentPage;

  return {
    data: items ?? lastFetched,
    isPlaceholderData: isAwaitingCurrentPage && lastFetched !== undefined,
    error: q.error,
    status: q.status,
    // `isFetchingNextPage` flips only after the prefetch effect runs, leaving a
    // render with no page and no fetch — long enough to paint an empty grid. The
    // `hasNextPage` guard stops a jump past the last page from loading forever.
    isLoading: q.isLoading || (isAwaitingCurrentPage && q.hasNextPage),
    isFetching: q.isFetching && !isPrefetching,
    hasNextPage,
  };
}
